import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { PageFrame } from "@/components/page-frame";

type Section = { id: string; title: string; body: React.ReactNode };

function LegalShell({
  icon: Icon, eyebrow, title, updated, sections
}: { icon: typeof Scale; eyebrow: string; title: string; updated: string; sections: Section[] }) {
  return (
    <PageFrame surface="marketing">
      <div className="relative pt-32 pb-16 px-6">
        <div aria-hidden className="absolute inset-x-0 top-0 h-[400px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(201,169,106,0.15), transparent 60%)" }} />

        <div className="relative mx-auto max-w-5xl">
          <BlurReveal>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-fg-2 hover:text-fg mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-accent/10 border border-accent-lo text-accent">
                <Icon className="w-5 h-5" />
              </span>
              <Badge variant="outline">{eyebrow}</Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
            <p className="mt-3 text-sm text-fg-3">Last updated {updated}</p>
          </BlurReveal>

          <div className="mt-10 grid lg:grid-cols-[220px_1fr] gap-10">
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <BlurReveal delay={0.1}>
                <nav aria-label="Sections" className="rounded-2xl border border-line bg-bg-2 p-3">
                  <div className="text-xs uppercase tracking-widest text-fg-3 px-3 py-2">Contents</div>
                  <ul className="space-y-0.5">
                    {sections.map(s => (
                      <li key={s.id}>
                        <a href={`#${s.id}`} className="block px-3 py-2 rounded-lg text-sm text-fg-2 hover:text-fg hover:bg-bg-3">
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </BlurReveal>
            </aside>

            <article className="space-y-10">
              {sections.map((s, i) => (
                <BlurReveal key={s.id} delay={0.05 * i}>
                  <section id={s.id} className="scroll-mt-32">
                    <h2 className="text-xl md:text-2xl font-bold tracking-tight">{s.title}</h2>
                    <div className="mt-3 text-fg-2 leading-relaxed space-y-3 text-sm md:text-base">{s.body}</div>
                  </section>
                  {i < sections.length - 1 && <Separator className="mt-10" />}
                </BlurReveal>
              ))}
            </article>
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

export function Terms() {
  return (
    <LegalShell icon={Scale} eyebrow="Legal" title="Terms of Service" updated="03 Jun 2026"
      sections={[
        { id: "use", title: "1. Acceptable use",
          body: <>
            <p>Arabise is a study tool for GCSE Arabic. By creating an account you agree to use it lawfully, not to attempt to disrupt it, and not to share your login.</p>
            <p>You're responsible for keeping your password secure. Tell us at <a className="text-accent hover:underline" href="mailto:support@arabise.co.uk">support@arabise.co.uk</a> if you think someone else has accessed your account.</p>
          </> },
        { id: "account", title: "2. Your account",
          body: <p>Accounts are personal. Keep your login credentials to yourself — you're responsible for anything done under your account.</p> },
        { id: "billing", title: "3. Billing & subscriptions",
          body: <p>Plus subscriptions are charged monthly via Stripe. Cancel anytime in your Profile — access continues to the end of the billing period.</p> },
        { id: "content", title: "4. Content & accuracy",
          body: <p>We do our best to align with the exam board specs, but we're not affiliated with AQA or Edexcel. Always double-check against your class materials.</p> },
        { id: "termination", title: "5. Termination",
          body: <p>You can delete your account anytime. We may suspend accounts that breach these terms. See our <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link> for how data is handled on deletion.</p> },
        { id: "liability", title: "6. Liability",
          body: <p>Arabise is provided "as is". We aren't liable for indirect losses (missed grade boundaries, server downtime, etc.). Nothing here limits liability for things UK law won't let us limit.</p> },
        { id: "contact", title: "7. Contact",
          body: <p>Questions? <a className="text-accent hover:underline" href="mailto:support@arabise.co.uk">support@arabise.co.uk</a></p> }
      ]} />
  );
}

export function Privacy() {
  return (
    <LegalShell icon={ShieldCheck} eyebrow="Privacy" title="Privacy Policy" updated="03 Jun 2026"
      sections={[
        { id: "what", title: "1. What we collect",
          body: <p>Your email, an optional name, your study progress (which words you've learned, points, streak), and basic usage telemetry (page views, errors) to keep the site working.</p> },
        { id: "why", title: "2. Why we collect it",
          body: <ul className="list-disc pl-5 space-y-1.5">
            <li>To run your account and sync progress across devices.</li>
            <li>To show leaderboards (your name + points are visible to other users).</li>
            <li>To debug issues. We never sell your data.</li>
          </ul> },
        { id: "third", title: "3. Third-party services",
          body: <p>Supabase (Auth + DB, EU region), Cloudflare (CDN + Pages), Stripe (billing). Each only sees the data needed for its job — see their privacy notices for detail.</p> },
        { id: "rights", title: "4. Your rights (UK GDPR)",
          body: <p>You can export, correct, or delete your data anytime. Email us at <a className="text-accent hover:underline" href="mailto:privacy@arabise.co.uk">privacy@arabise.co.uk</a> if any settings don't let you do what you need.</p> },
        { id: "retention", title: "5. Retention",
          body: <p>Account data is held while your account exists. On deletion we keep it for 30 days (for recovery) then erase it. Anonymised aggregates may be kept for analytics.</p> }
      ]} />
  );
}
